// @ts-ignore
import { defineStore } from 'pinia';
import { useLocalesStore } from "@/stores/i18n"
import { getLocaleCookie } from "@/utils/cookies";

export type User = {
  name: string;
  email: string;
  avatar: string;
  slug: string;
  must_verify_email: boolean;
  has_password: boolean;
  roles: string[];
  providers: string[];
  profile: Record<string, any>;
  preferred_language: string;
  account_status: string;
};

export const useAuthStore = defineStore('auth', () => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const settingsStore = useSettingsStore();
  const isFetchingUser = ref(false);
  const localesStore = useLocalesStore()
  const { setLocale } = localesStore

  // Define user state as a reference
  const user = ref<User>({
    name: '',
    email: '',
    avatar: '',
    slug: '',
    must_verify_email: false,
    has_password: false,
    roles: [],
    providers: [],
    profile: {},
    preferred_language: '',
    account_status: 'incomplete',
  });

  // Cookie to store user token
  const token = useCookie('token', {
    path: '/',
    sameSite: 'strict',
    secure: config.public.apiBase.startsWith('https://'),
    maxAge: 60 * 60 * 24 * 365,
  });

  // Computed property to check if the user is logged in
  const logged = computed(() => !!token.value);

  const setPreferredLanguage = (locale) => {
    setLocale(locale);
  };

  // Logout function using useFetch
  const { refresh: logout } = useFetch<any>('logout', {
    method: 'POST',
    immediate: false,
    onResponse({ response }) {
      if (response.status === 200) {
        const localeFromCookie = getLocaleCookie();

        const redirectUrl = localeFromCookie === 'en' ? '/auth/login' : `/${localeFromCookie}/auth/login`;

        // Clear token and user state
        token.value = '';

        // Clear authenticated settings
        settingsStore.authSettings = {};
        settingsStore.mergeSettings();

        // Redirect to login page
        nuxtApp.runWithContext(() => {
          return navigateTo(redirectUrl);
        });

      }
    },
    onResponseError({ error }) {
      console.error('Logout failed or API is unreachable:', error);
    },
  });

  // Fetch user function using useFetch
  const { refresh: fetchUser } = useFetch<any>('user', {
    immediate: false,
    async onResponse({ response }) {
      if (response.status === 200) {
        user.value = {
          ...response._data.data.user,
          profile: {}, // Initialize profile as empty
        };

        // Set preferred language globally
        setPreferredLanguage(user.value.preferred_language)

        //settingsStore.fetchAuthSettings();

        // Only fetch profile if allowed
        //if (!isFetchingUser.value) {
        //isFetchingUser.value = true;
        await fetchProfile().then(() => {
          //console.log('Profile fetched successfully');
          isFetchingUser.value = false;
        }).catch(error => {
          //console.error('Failed to fetch profile:', error);
          isFetchingUser.value = false;
          handleLogout();
        });
        //}
      }
    },
    onResponseError({ error }) {
      console.error('Failed to fetch user or API is unreachable:', error);
      handleLogout();
    },
  });


// Fetch profile function using useFetch
  const { refresh: fetchProfile } = useFetch<any>('profile', {
    immediate: false,
    onResponse({ response }) {
      if (response.status === 200 && !isFetchingUser.value) {
        user.value = {
          ...user.value,
          profile: response._data.data || {},
        };
        setPreferredLanguage(user.value.preferred_language)
      }
    },
    onResponseError({ error }) {
      console.error('Failed to fetch profile or API is unreachable:', error);
      handleLogout();
    },
  });

  // Handle logout when an error occurs
  const handleLogout = () => {

    const localeFromCookie = getLocaleCookie();

    // Clear session and user data
    token.value = '';
    user.value = <User>{
      name: '',
      email: '',
      avatar: '',
      slug: '',
      must_verify_email: false,
      has_password: false,
      roles: [],
      providers: [],
      profile: {},
      account_status: 'incomplete',
    };

    settingsStore.authSettings = {}; // Clear auth settings on logout
    settingsStore.mergeSettings();

    // Construct the redirect URL
    const redirectUrl = localeFromCookie === 'en' ? '/auth/login' : `/${localeFromCookie}/auth/login`;

    // Redirect to the localized login page
    return nuxtApp.runWithContext(() => navigateTo(redirectUrl));
  };


  const clear = () => {
    token.value = '';
    user.value = <User>{
      name: '',
      email: '',
      avatar: '',
      slug: '',
      must_verify_email: false,
      has_password: false,
      roles: [],
      providers: [],
      profile: {},
      account_status: 'incomplete',
    };

    settingsStore.authSettings = {}; // Clear auth settings on logout
    settingsStore.mergeSettings();
  }

  return { user, logged, logout, fetchUser, fetchProfile, token, clear, setPreferredLanguage };
});
